import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Tooltip,
} from '@mantine/core';

import {GroupedDelegation} from '../zoneGrouping';
import {ZoneDelegationStatus} from '../types';

// FNP-1423 / FNP-1378 — WHAT THE ZONE'S PARENT ACTUALLY SAYS.
//
// ⚠️ FOUR STATUSES AND A DISAGREEMENT, AND NOT ONE IS A STYLED VERSION OF
// ANOTHER. This renders `delegationStatus`, the API's answer obtained over
// public DNS, which is a different question from the `appearsDelegated`
// tri-state this badge used to show: that one compared two facts the platform
// already stored, this one asks the delegation chain what it yields.
//
// ⚠️ NOT_DELEGATED CARRIES REAL WEIGHT, DELIBERATELY. It means the name
// resolves somewhere else or holds no NS record at all — the zone exists, costs
// money and serves nobody. It is the only status that means "safe to delete",
// and an operator has to be able to spot it while scanning. A grey neutral
// badge would bury the one row on the page that needs acting on.
//
// ⚠️ AND NOT_DETERMINED IS NEVER STYLED LIKE IT. Collapsing the two turns a DNS
// timeout into a green light for deleting a live, correctly-delegated
// production zone — the exact failure the non-null enum exists to prevent. It
// stays neutral, because it is an absence of information rather than a problem,
// and it carries `delegationUndeterminedReason` so the operator learns WHY
// instead of reading a bare "unknown".
//
// ⚠️ PARTIAL IS HANDLED RATHER THAN FALLING THROUGH TO A BLANK CELL. The
// nameserver sets overlap but disagree — a half-applied delegation change,
// where some queries land here and some do not. NOT safe to delete, and not a
// synonym for either neighbour.
//
// Its own file because a module that also exports a hook or a constant breaks
// Fast Refresh, and this lints at --max-warnings 0.

type BadgeSpec = {
    color: string,
    variant: string,
    label: string,
    tooltip?: string,
};

const STATUS_BADGE: Record<ZoneDelegationStatus, BadgeSpec> = {
    DELEGATED: {
        color: 'green',
        variant: 'light',
        label: 'Delegated',
        tooltip: 'The delegation chain yields exactly the nameservers this zone publishes.',
    },
    // ⚠️ FILLED RED, not a light tint. This is the status that means the zone
    // serves nobody, and it must not read as one more grey pill in a column of
    // grey pills.
    NOT_DELEGATED: {
        color: 'red',
        variant: 'filled',
        label: 'Not delegated',
        tooltip: 'This name resolves somewhere else, or holds no NS record at all. The zone exists but serves nobody.',
    },
    // A real conclusion, and a warning rather than a failure: something is
    // half-applied, so it is NOT safe to delete.
    PARTIAL: {
        color: 'orange',
        variant: 'filled',
        label: 'Partly delegated',
        tooltip: 'The parent nameservers overlap this zone but do not match. A delegation change may be half applied, so some queries reach this zone and some do not.',
    },
    // Neutral on purpose — an absence of information, not a warning. Its
    // tooltip is replaced by the API's own reason wherever one arrived.
    NOT_DETERMINED: {
        color: 'gray',
        variant: 'outline',
        label: 'Not determined',
        tooltip: 'Delegation was not determined for this zone. This is not the same as "not delegated".',
    },
};

export const GroupedDelegationBadge = ({delegation}: { delegation: GroupedDelegation }) => {

    if (delegation.kind === 'DISAGREED') {
        return (
            <Tooltip
                label={I18n.get('The connections that reach this zone disagree about its delegation. Open the zone to read one connection at a time.')}
                withArrow={true}
                multiline={true}
                w={280}
            >
                <Badge variant={'filled'} color={'orange'}>
                    {I18n.get('Connections disagree')}
                </Badge>
            </Tooltip>
        );
    }

    const badge = STATUS_BADGE[delegation.status];

    // ⚠️ THE API'S OWN SENTENCE WINS OVER THE GENERIC ONE. "The parent could not
    // be reached (ESERVFAIL). This is not evidence that the zone is
    // undelegated." is what stops someone deleting a live zone; "not
    // determined" on its own is what makes people ignore the column. Only
    // NOT_DETERMINED ever carries a reason — the schema says the field is null
    // on every other status.
    const tooltip = delegation.reason ?? badge.tooltip;

    const rendered = (
        <Badge variant={badge.variant} color={badge.color}>
            {I18n.get(badge.label)}
        </Badge>
    );

    if (!tooltip) {
        return rendered;
    }

    return (
        <Tooltip label={I18n.get(tooltip)} withArrow={true} multiline={true} w={280}>
            {rendered}
        </Tooltip>
    );
};
